import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { formatPrice, formatDate } from '../../utils';
import { useAuth } from '../../contexts/AuthContext';
import vendorDataService from '../../services/vendorDataService';
import bookingService from '../../services/bookingService';
import invoiceService from '../../services/invoiceService';
import inquiryService from '../../services/inquiryService';
import InvoiceManagement from './components/InvoiceManagement';
import ServiceModal from './components/ServiceModal';
import VendorViewsAnalytics from './components/VendorViewsAnalytics';

const VendorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [vendor, setVendor] = useState(null);
  const [subscription, setSubscription] = useState({
    type: 'free',
    startDate: new Date().toISOString().split('T')[0],
    endDate: null,
    price: 0,
    features: []
  });

  const [metrics, setMetrics] = useState({
    profileViews: 0,
    profileViewsGrowth: 0,
    inquiries: 0,
    inquiriesGrowth: 0,
    bookings: 0,
    bookingsGrowth: 0,
    revenue: 0,
    revenueGrowth: 0,
    responseRate: 0,
    conversionRate: 0,
    searchRanking: 0
  });

  // Service listings
  const [services, setServices] = useState([]);

  // Active bookings
  const [bookings, setBookings] = useState([]);

  // Messages/inquiries
  const [messages, setMessages] = useState([]);

  // Financial data
  const [financials, setFinancials] = useState({
    totalEarnings: 0,
    monthlyEarnings: 0,
    pendingPayments: 0,
    upcomingBookings: 0,
    averageBookingValue: 0,
    monthlyGrowth: 0,
    paymentHistory: []
  });

  useEffect(() => {
    loadVendorData();
  }, [user]);

  const loadVendorData = async () => {
    if (!user?.id) {
      navigate('/sign-in-page');
      return;
    }

    setIsLoading(true);

    try {
      // Load vendor profile
      const vendorResult = await vendorDataService.getVendorByUserId(user.id);
      if (!vendorResult.success || !vendorResult.data) {
        // User is not a vendor, redirect to user dashboard
        navigate('/user-dashboard');
        return;
      }

      const vendorData = vendorResult.data;

      setVendor({
        id: vendorData.id,
        name: vendorData.business_name,
        email: user.email,
        phone: vendorData.phone || user.phone,
        businessName: vendorData.business_name,
        category: vendorData.category,
        specialties: vendorData.subcategories || [],
        location: `${vendorData.city}, ${vendorData.state}`,
        profileImage: user.profile_image || '',
        coverImage: vendorData.cover_image || '',
        rating: vendorData.average_rating || 0,
        reviewCount: vendorData.total_reviews || 0,
        yearsExperience: vendorData.years_experience || 0,
        joinDate: vendorData.created_at?.split('T')[0] || '',
        verified: vendorData.is_verified || false,
        responseTime: vendorData.response_time || 'N/A',
        completedBookings: vendorData.total_bookings || 0,
        languages: vendorData.languages || [],
        serviceArea: `${vendorData.city}, ${vendorData.state}`,
        websiteUrl: vendorData.website || '',
        instagramUrl: vendorData.social_media?.instagram || ''
      });

      // Set subscription based on vendor data
      setSubscription({
        type: vendorData.subscription_type || 'free',
        startDate: vendorData.subscription_start_date || new Date().toISOString().split('T')[0],
        endDate: vendorData.subscription_end_date || null,
        price: vendorData.subscription_type === 'premium' ? 299 : vendorData.subscription_type === 'basic' ? 99 : 0,
        features: getSubscriptionFeatures(vendorData.subscription_type)
      });

      // Load analytics if available
      const [analyticsResult, rankingResult] = await Promise.all([
        vendorDataService.getVendorAnalytics(vendorData.id),
        vendorDataService.calculateSearchRanking(vendorData.id)
      ]);

      if (analyticsResult.success) {
        const analytics = analyticsResult.data;
        const ranking = rankingResult.success ? rankingResult.data : null;

        setMetrics({
          profileViews: analytics.profileViews || 0,
          profileViewsGrowth: analytics.profileViewsGrowth || 0,
          inquiries: analytics.inquiries || 0,
          inquiriesGrowth: analytics.inquiriesGrowth || 0,
          bookings: analytics.bookings || 0,
          bookingsGrowth: analytics.bookingsGrowth || 0,
          revenue: analytics.revenue || 0,
          revenueGrowth: analytics.revenueGrowth || 0,
          responseRate: analytics.responseRate || 0,
          conversionRate: analytics.conversionRate || 0,
          searchRanking: ranking?.rankingScore || 0,
          searchRankingPosition: ranking?.estimatedPosition || 0,
          searchRankingPercentile: ranking?.percentile || 0
        });

        // Update financials
        setFinancials(prev => ({
          ...prev,
          totalEarnings: analytics.revenue || 0,
          monthlyEarnings: analytics.monthlyRevenue || 0,
          monthlyGrowth: analytics.revenueGrowth || 0,
          averageBookingValue: analytics.bookings > 0
            ? (analytics.revenue / analytics.bookings)
            : 0
        }));
      }

      // Load services/packages from vendor data (already included in the query)
      if (vendorData.id && vendorData.vendor_packages) {
        setServices(vendorData.vendor_packages.map(pkg => ({
          id: pkg.id,
          name: pkg.name,
          description: pkg.description,
          price: pkg.price,
          duration: pkg.duration || 'N/A',
          includes: pkg.features || [],
          active: pkg.is_active,
          bookings: 0, // Will be loaded separately
          views: 0 // Will be loaded separately
        })));
      }

      // Load bookings
      if (vendorData.id) {
        const bookingsResult = await bookingService.getVendorBookings(vendorData.id);
        if (bookingsResult.success && bookingsResult.data) {
          setBookings(bookingsResult.data.map(booking => ({
            id: booking.id,
            clientName: booking.user_profiles?.full_name || 'Unknown Client',
            event: booking.event_type || 'Event',
            date: booking.event_date,
            time: booking.event_time || 'TBD',
            location: booking.event_location || 'TBD',
            price: booking.total_amount,
            status: booking.status,
            clientPhone: booking.user_profiles?.phone || '',
            clientEmail: booking.user_profiles?.email || '',
            notes: booking.special_requirements || ''
          })));
        }

        // Load messages/inquiries (importing inquiryService)
        const inquiriesResult = await inquiryService.getVendorInquiries(vendorData.id);
        if (inquiriesResult.success && inquiriesResult.data) {
          setMessages(inquiriesResult.data.map(inquiry => ({
            id: inquiry.id,
            clientName: inquiry.user?.full_name || 'Unknown',
            subject: inquiry.subject,
            message: inquiry.message,
            date: inquiry.created_at?.split('T')[0],
            time: new Date(inquiry.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
            status: inquiry.status,
            priority: inquiry.priority || 'medium',
            weddingDate: inquiry.event_date,
            budget: inquiry.budget,
            guestCount: inquiry.guest_count
          })));
        }

        // Calculate financials from bookings and invoices
        const invoicesResult = await invoiceService.getVendorInvoices(vendorData.id);
        if (invoicesResult.success && invoicesResult.data) {
          const invoices = invoicesResult.data;
          const totalEarnings = invoices
            .filter(inv => inv.status === 'paid')
            .reduce((sum, inv) => sum + inv.total_amount, 0);

          const currentMonth = new Date().getMonth();
          const currentYear = new Date().getFullYear();
          const monthlyEarnings = invoices
            .filter(inv => {
              const invDate = new Date(inv.created_at);
              return inv.status === 'paid' &&
                invDate.getMonth() === currentMonth &&
                invDate.getFullYear() === currentYear;
            })
            .reduce((sum, inv) => sum + inv.total_amount, 0);

          const pendingPayments = invoices
            .filter(inv => inv.status === 'pending' || inv.status === 'sent')
            .reduce((sum, inv) => sum + inv.total_amount, 0);

          setFinancials({
            totalEarnings,
            monthlyEarnings,
            pendingPayments,
            upcomingBookings: bookingsResult.data
              ?.filter(b => b.status === 'confirmed')
              .reduce((sum, b) => sum + (b.total_amount || 0), 0) || 0,
            averageBookingValue: totalEarnings / (bookingsResult.data?.length || 1),
            monthlyGrowth: 0, // Calculate based on historical data
            paymentHistory: invoices.slice(0, 3).map(inv => ({
              date: inv.created_at?.split('T')[0],
              amount: inv.total_amount,
              client: inv.client_name,
              status: inv.status
            }))
          });
        }
      }
    } catch (error) {
      console.error('Error loading vendor data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSubscriptionFeatures = (type) => {
    const features = {
      free: ['Basic listing', 'Up to 5 images', 'Email support'],
      basic: ['Enhanced listing', 'Up to 20 images', 'Priority support', 'Basic analytics'],
      premium: ['Unlimited listings', 'Priority search placement', 'Advanced analytics', 'Instant booking', 'Customer support', 'Marketing tools'],
      enterprise: ['All premium features', 'Dedicated account manager', 'Custom integrations', 'White-label options']
    };
    return features[type] || features.free;
  };

  // Navigation tabs
  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'listings', label: 'Manage Listings', icon: 'Package' },
    { id: 'bookings', label: 'Bookings', icon: 'Calendar' },
    { id: 'invoices', label: 'Invoices', icon: 'FileText' },
    { id: 'messages', label: 'Messages', icon: 'MessageSquare' },
    { id: 'financials', label: 'Financials', icon: 'DollarSign' },
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'subscription', label: 'Subscription', icon: 'Crown' }
  ];

  // Handle booking status updates
  const handleUpdateBookingStatus = (bookingId, newStatus) => {
    setBookings(prev => prev?.map(booking =>
      booking?.id === bookingId ? { ...booking, status: newStatus } : booking
    ));
  };

  // Handle message actions
  const handleMarkAsRead = (messageId) => {
    setMessages(prev => prev?.map(message =>
      message?.id === messageId ? { ...message, status: 'read' } : message
    ));
  };

  const handleReplyMessage = (messageId) => {
    navigate('/message-detail', { state: { messageId } });
    handleMarkAsRead(messageId);
  };

  // Handle service management
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);

  const handleToggleService = async (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (!service || !vendor?.id) return;

    const result = await vendorDataService.updateVendorPackage(serviceId, {
      is_active: !service.active
    });

    if (result.success) {
      setServices(prev => prev?.map(s =>
        s?.id === serviceId ? { ...s, active: !s?.active } : s
      ));
      await loadVendorData(); // Reload to get fresh data
    }
  };

  const handleEditService = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    setEditingService(service);
    setShowServiceModal(true);
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    if (vendor?.id) {
      const result = await vendorDataService.deleteVendorPackage(serviceId);
      if (result.success) {
        setServices(prev => prev.filter(s => s.id !== serviceId));
      }
    }
  };

  const handleAddService = () => {
    setEditingService(null);
    setShowServiceModal(true);
  };

  const handleSaveService = async (serviceData) => {
    if (!vendor?.id) return;

    if (editingService) {
      // Update existing
      const result = await vendorDataService.updateVendorPackage(editingService.id, serviceData);
      if (result.success) {
        await loadVendorData();
      }
    } else {
      // Create new
      const result = await vendorDataService.createVendorPackage({
        vendor_id: vendor.id,
        ...serviceData
      });
      if (result.success) {
        await loadVendorData();
      }
    }
    setShowServiceModal(false);
    setEditingService(null);
  };

  // Handle subscription upgrade
  const handleUpgradeSubscription = (newPlan) => {
    navigate('/vendor-profile', { state: { openSubscription: true, plan: newPlan } });
  };

  // Filter states
  const [bookingFilter, setBookingFilter] = useState('all');
  const [messageFilter, setMessageFilter] = useState('all');
  const [showBookingFilters, setShowBookingFilters] = useState(false);
  const [showMessageFilters, setShowMessageFilters] = useState(false);

  // Export bookings to CSV
  const handleExportBookings = () => {
    const headers = ['Client Name', 'Event', 'Date', 'Time', 'Location', 'Price', 'Status'];
    const rows = bookings.map(b => [
      b.clientName,
      b.event,
      b.date,
      b.time,
      b.location,
      `$${b.price}`,
      b.status
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Archive messages
  const handleArchiveMessages = async () => {
    const unreadMessages = messages.filter(m => m.status === 'unread');
    if (unreadMessages.length === 0) return;

    const inquiryIds = unreadMessages.map(m => m.id);
    if (vendor?.id) {
      const result = await inquiryService.archiveInquiries(inquiryIds, vendor.id);
      if (result.success) {
        await loadVendorData();
      }
    }
  };

  // Update profile
  const handleUpdateProfile = async (profileData) => {
    if (!vendor?.id) return;

    const result = await vendorDataService.updateVendorProfile(vendor.id, profileData);
    if (result.success) {
      await loadVendorData();
    }
  };

  // Filtered bookings
  const filteredBookings = bookings.filter(booking => {
    if (bookingFilter === 'all') return true;
    return booking.status === bookingFilter;
  });

  // Filtered messages
  const filteredMessages = messages.filter(message => {
    if (messageFilter === 'all') return true;
    if (messageFilter === 'unread') return message.status === 'unread';
    if (messageFilter === 'important') return message.priority === 'high';
    if (messageFilter === 'archived') return message.status === 'archived';
    return true;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-text-secondary">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Vendor Dashboard - Grow Your Wedding Business | ShaadiNest</title>
        <meta name="description" content="Manage your wedding vendor business with comprehensive analytics, booking management, and growth tools." />
      </Helmet>

      <Header />

      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-soft p-6 sticky top-24">
                {/* Vendor Profile Summary */}
                <div className="text-center mb-6">
                  <div className="relative mx-auto mb-4">
                    <img
                      src={vendor?.profileImage}
                      alt={vendor?.name}
                      className="w-20 h-20 rounded-full object-cover mx-auto border-4 border-primary-100"
                    />
                    {vendor?.verified && (
                      <div className="absolute bottom-0 right-1/2 transform translate-x-1/2 translate-y-1/2 w-6 h-6 bg-blue-500 rounded-full border-2 border-white flex items-center justify-center">
                        <Icon name="CheckCircle" size={16} className="text-white" />
                      </div>
                    )}
                  </div>
                  <h2 className="font-heading font-semibold text-xl text-text-primary">
                    {vendor?.businessName}
                  </h2>
                  <p className="text-text-secondary text-sm">{vendor?.category}</p>
                  <div className="flex items-center justify-center space-x-2 mt-2">
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={16} className="text-yellow-500" fill="currentColor" />
                      <span className="text-sm font-medium">{vendor?.rating}</span>
                    </div>
                    <span className="text-text-muted">•</span>
                    <span className="text-sm text-text-secondary">{vendor?.reviewCount} reviews</span>
                  </div>
                </div>

                {/* Navigation */}
                <nav className="space-y-2">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${activeTab === tab?.id
                        ? 'bg-primary text-white shadow-soft'
                        : 'text-text-secondary hover:bg-surface-100'
                        }`}
                    >
                      <Icon name={tab?.icon} size={20} />
                      <span className="font-medium">{tab?.label}</span>
                      {tab?.id === 'messages' && messages?.filter(m => m?.status === 'unread')?.length > 0 && (
                        <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-1">
                          {messages?.filter(m => m?.status === 'unread')?.length}
                        </span>
                      )}
                    </button>
                  ))}
                </nav>

                {/* Quick Actions */}
                <div className="mt-6 pt-6 border-t border-border-light space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Plus"
                    fullWidth
                    onClick={() => setActiveTab('listings')}
                  >
                    Add Service
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    iconName="ExternalLink"
                    fullWidth
                    onClick={() => navigate('/vendor-profile')}
                  >
                    View Public Profile
                  </Button>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Invoice Management Tab */}
              {activeTab === 'invoices' && <InvoiceManagement />}

              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Profile Views Analytics */}
                  {vendor?.id && <VendorViewsAnalytics vendorId={vendor.id} />}

                  {/* Performance Overview */}
                  <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-heading font-semibold text-2xl">
                          Welcome back, {vendor?.name}!
                        </h3>
                        <p className="text-white/90">
                          Your business is growing {metrics?.revenueGrowth}% this month
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold">{formatPrice(metrics?.revenue)}</div>
                        <div className="text-white/80">Monthly Revenue</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold">{metrics?.profileViews}</div>
                        <div className="text-white/80 text-sm">Profile Views</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold">{metrics?.inquiries}</div>
                        <div className="text-white/80 text-sm">Inquiries</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold">{metrics?.bookings}</div>
                        <div className="text-white/80 text-sm">Bookings</div>
                      </div>
                    </div>
                  </div>

                  {/* Key Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl shadow-soft p-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Icon name="Eye" size={24} className="text-blue-600" />
                        </div>
                        <div className="text-green-600 text-sm font-medium">
                          +{metrics?.profileViewsGrowth}%
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-text-primary">{metrics?.profileViews}</div>
                      <div className="text-text-secondary text-sm">Profile Views</div>
                    </div>

                    <div className="bg-white rounded-xl shadow-soft p-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                          <Icon name="MessageCircle" size={24} className="text-green-600" />
                        </div>
                        <div className="text-green-600 text-sm font-medium">
                          +{metrics?.inquiriesGrowth}%
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-text-primary">{metrics?.inquiries}</div>
                      <div className="text-text-secondary text-sm">New Inquiries</div>
                    </div>

                    <div className="bg-white rounded-xl shadow-soft p-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Icon name="Calendar" size={24} className="text-purple-600" />
                        </div>
                        <div className="text-green-600 text-sm font-medium">
                          +{metrics?.bookingsGrowth}%
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-text-primary">{metrics?.bookings}</div>
                      <div className="text-text-secondary text-sm">Bookings</div>
                    </div>

                    <div className="bg-white rounded-xl shadow-soft p-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Icon name="TrendingUp" size={24} className="text-orange-600" />
                        </div>
                        <div className="text-text-secondary text-sm font-medium">
                          {metrics?.conversionRate}%
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-text-primary">{metrics?.responseRate}%</div>
                      <div className="text-text-secondary text-sm">Response Rate</div>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Bookings */}
                    <div className="bg-white rounded-xl shadow-soft p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading font-semibold text-lg text-text-primary">
                          Recent Bookings
                        </h3>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setActiveTab('bookings')}
                        >
                          View All
                        </Button>
                      </div>
                      <div className="space-y-4">
                        {bookings?.slice(0, 3)?.map((booking) => (
                          <div key={booking?.id} className="flex items-center justify-between p-3 bg-surface-50 rounded-lg">
                            <div>
                              <p className="font-medium text-text-primary">{booking?.clientName}</p>
                              <p className="text-text-secondary text-sm">{booking?.event}</p>
                              <p className="text-text-muted text-xs">{formatDate(booking?.date)}</p>
                            </div>
                            <div className="text-right">
                              <div className="text-text-primary font-medium">{formatPrice(booking?.price)}</div>
                              <div className={`text-xs px-2 py-1 rounded-full ${booking?.status === 'confirmed' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'
                                }`}>
                                {booking?.status}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recent Messages */}
                    <div className="bg-white rounded-xl shadow-soft p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading font-semibold text-lg text-text-primary">
                          Recent Messages
                        </h3>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setActiveTab('messages')}
                        >
                          View All
                        </Button>
                      </div>
                      <div className="space-y-4">
                        {messages?.slice(0, 3)?.map((message) => (
                          <div key={message?.id} className="flex items-start space-x-3 p-3 bg-surface-50 rounded-lg">
                            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                              <Icon name="User" size={16} className="text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="font-medium text-text-primary truncate">{message?.clientName}</p>
                                {message?.status === 'unread' && (
                                  <div className="w-2 h-2 bg-red-500 rounded-full ml-2"></div>
                                )}
                              </div>
                              <p className="text-text-secondary text-sm truncate">{message?.subject}</p>
                              <p className="text-text-muted text-xs">{formatDate(message?.date)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Manage Listings Tab */}
              {activeTab === 'listings' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading font-bold text-2xl text-text-primary">
                      Manage Listings
                    </h2>
                    <Button
                      variant="primary"
                      iconName="Plus"
                      onClick={handleAddService}
                    >
                      Add New Service
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {services?.map((service) => (
                      <div key={service?.id} className="bg-white rounded-xl shadow-soft p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-heading font-semibold text-lg text-text-primary">
                                {service?.name}
                              </h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${service?.active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                                }`}>
                                {service?.active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <p className="text-text-secondary mb-3">{service?.description}</p>
                            <div className="flex flex-wrap gap-2">
                              {service?.includes?.map((include, index) => (
                                <span key={index} className="px-2 py-1 bg-surface-100 text-text-secondary text-xs rounded-full">
                                  {include}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-text-primary">{formatPrice(service?.price)}</div>
                            <div className="text-text-secondary text-sm">{service?.duration}</div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-sm text-text-secondary">
                            <span>{service?.bookings} bookings</span>
                            <span>{service?.views} views</span>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleService(service?.id)}
                            >
                              {service?.active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="Edit"
                              onClick={() => handleEditService(service?.id)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="Trash2"
                              onClick={() => handleDeleteService(service?.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Bookings Tab */}
              {activeTab === 'bookings' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading font-bold text-2xl text-text-primary">
                      Active Bookings
                    </h2>
                    <div className="flex space-x-2">
                      <div className="relative">
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Filter"
                          onClick={() => setShowBookingFilters(!showBookingFilters)}
                        >
                          Filter {bookingFilter !== 'all' && `(${bookingFilter})`}
                        </Button>
                        {showBookingFilters && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-border z-10">
                            {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(status => (
                              <button
                                key={status}
                                onClick={() => {
                                  setBookingFilter(status);
                                  setShowBookingFilters(false);
                                }}
                                className="w-full text-left px-4 py-2 hover:bg-surface-100 first:rounded-t-lg last:rounded-b-lg capitalize"
                              >
                                {status}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Download"
                        onClick={handleExportBookings}
                      >
                        Export
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {filteredBookings?.map((booking) => (
                      <div key={booking?.id} className="bg-white rounded-xl shadow-soft p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-heading font-semibold text-lg text-text-primary">
                                {booking?.clientName}
                              </h3>
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${booking?.status === 'confirmed' ? 'bg-green-100 text-green-600' :
                                booking?.status === 'pending' ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-600'
                                }`}>
                                {booking?.status}
                              </span>
                            </div>
                            <p className="text-text-secondary mb-2">{booking?.event}</p>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-text-muted">Date & Time</p>
                                <p className="text-text-primary font-medium">
                                  {formatDate(booking?.date)} at {booking?.time}
                                </p>
                              </div>
                              <div>
                                <p className="text-text-muted">Location</p>
                                <p className="text-text-primary font-medium">{booking?.location}</p>
                              </div>
                              <div>
                                <p className="text-text-muted">Contact</p>
                                <p className="text-text-primary font-medium">{booking?.clientPhone}</p>
                              </div>
                              <div>
                                <p className="text-text-muted">Email</p>
                                <p className="text-text-primary font-medium">{booking?.clientEmail}</p>
                              </div>
                            </div>
                            {booking?.notes && (
                              <div className="mt-3 p-3 bg-surface-50 rounded-lg">
                                <p className="text-text-muted text-sm">Notes:</p>
                                <p className="text-text-primary text-sm">{booking?.notes}</p>
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-text-primary">{formatPrice(booking?.price)}</div>
                            <div className="text-text-secondary text-sm">Service Fee</div>
                          </div>
                        </div>

                        <div className="flex space-x-2 mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Phone"
                            onClick={() => window.location.href = `tel:${booking?.clientPhone}`}
                          >
                            Call Client
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="MessageSquare"
                            onClick={() => navigate('/message-detail', { state: { messageId: booking?.id } })}
                          >
                            Message
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Mail"
                            onClick={() => window.location.href = `mailto:${booking?.clientEmail}`}
                          >
                            Email
                          </Button>
                          {booking?.status === 'pending' && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleUpdateBookingStatus(booking?.id, 'confirmed')}
                            >
                              Confirm Booking
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Messages Tab */}
              {activeTab === 'messages' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading font-bold text-2xl text-text-primary">
                      Messages & Inquiries
                    </h2>
                    <div className="flex space-x-2">
                      <div className="relative">
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Filter"
                          onClick={() => setShowMessageFilters(!showMessageFilters)}
                        >
                          Filter {messageFilter !== 'all' && `(${messageFilter})`}
                        </Button>
                        {showMessageFilters && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-border z-10">
                            {['all', 'unread', 'important', 'archived'].map(filter => (
                              <button
                                key={filter}
                                onClick={() => {
                                  setMessageFilter(filter);
                                  setShowMessageFilters(false);
                                }}
                                className="w-full text-left px-4 py-2 hover:bg-surface-100 first:rounded-t-lg last:rounded-b-lg capitalize"
                              >
                                {filter}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Archive"
                        onClick={handleArchiveMessages}
                      >
                        Archive Read
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {filteredMessages?.map((message) => (
                      <div key={message?.id} className={`bg-white rounded-xl shadow-soft p-6 ${message?.status === 'unread' ? 'border-l-4 border-primary' : ''
                        }`}>
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-heading font-semibold text-lg text-text-primary">
                                {message?.clientName}
                              </h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${message?.priority === 'high' ? 'bg-red-100 text-red-600' :
                                message?.priority === 'medium' ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-600'
                                }`}>
                                {message?.priority} priority
                              </span>
                              {message?.status === 'unread' && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full">
                                  New
                                </span>
                              )}
                            </div>
                            <p className="text-text-secondary font-medium mb-2">{message?.subject}</p>
                            <p className="text-text-primary mb-3">{message?.message}</p>

                            {message?.weddingDate && (
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                <div>
                                  <p className="text-text-muted">Wedding Date</p>
                                  <p className="text-text-primary font-medium">{formatDate(message?.weddingDate)}</p>
                                </div>
                                <div>
                                  <p className="text-text-muted">Budget</p>
                                  <p className="text-text-primary font-medium">{formatPrice(message?.budget)}</p>
                                </div>
                                {message?.guestCount && (
                                  <div>
                                    <p className="text-text-muted">Guests</p>
                                    <p className="text-text-primary font-medium">{message?.guestCount}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-text-secondary text-sm">
                              {formatDate(message?.date)}
                            </div>
                            <div className="text-text-muted text-sm">
                              {message?.time}
                            </div>
                          </div>
                        </div>

                        <div className="flex space-x-2">
                          <Button
                            variant="primary"
                            size="sm"
                            iconName="Reply"
                            onClick={() => handleReplyMessage(message?.id)}
                          >
                            Reply
                          </Button>
                          {message?.status === 'unread' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleMarkAsRead(message?.id)}
                            >
                              Mark as Read
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Archive"
                          >
                            Archive
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Financials Tab */}
              {activeTab === 'financials' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading font-bold text-2xl text-text-primary">
                      Financial Overview
                    </h2>
                    <Button variant="outline" size="sm" iconName="Download">
                      Export Report
                    </Button>
                  </div>

                  {/* Financial Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl shadow-soft p-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                          <Icon name="DollarSign" size={24} className="text-green-600" />
                        </div>
                        <div className="text-green-600 text-sm font-medium">
                          +{financials?.monthlyGrowth}%
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-text-primary">
                        {formatPrice(financials?.totalEarnings)}
                      </div>
                      <div className="text-text-secondary text-sm">Total Earnings</div>
                    </div>

                    <div className="bg-white rounded-xl shadow-soft p-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Icon name="TrendingUp" size={24} className="text-blue-600" />
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-text-primary">
                        {formatPrice(financials?.monthlyEarnings)}
                      </div>
                      <div className="text-text-secondary text-sm">This Month</div>
                    </div>

                    <div className="bg-white rounded-xl shadow-soft p-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <Icon name="Clock" size={24} className="text-yellow-600" />
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-text-primary">
                        {formatPrice(financials?.pendingPayments)}
                      </div>
                      <div className="text-text-secondary text-sm">Pending Payments</div>
                    </div>

                    <div className="bg-white rounded-xl shadow-soft p-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Icon name="Calendar" size={24} className="text-purple-600" />
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-text-primary">
                        {formatPrice(financials?.upcomingBookings)}
                      </div>
                      <div className="text-text-secondary text-sm">Upcoming Bookings</div>
                    </div>
                  </div>

                  {/* Payment History */}
                  <div className="bg-white rounded-xl shadow-soft p-6">
                    <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
                      Recent Payments
                    </h3>
                    <div className="space-y-4">
                      {financials?.paymentHistory?.map((payment, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-surface-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${payment?.status === 'paid' ? 'bg-green-100' : 'bg-yellow-100'
                              }`}>
                              <Icon
                                name={payment?.status === 'paid' ? 'CheckCircle' : 'Clock'}
                                size={20}
                                className={payment?.status === 'paid' ? 'text-green-600' : 'text-yellow-600'}
                              />
                            </div>
                            <div>
                              <p className="font-medium text-text-primary">{payment?.client}</p>
                              <p className="text-text-secondary text-sm">{formatDate(payment?.date)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-text-primary font-medium">{formatPrice(payment?.amount)}</div>
                            <div className={`text-sm px-2 py-1 rounded-full ${payment?.status === 'paid' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'
                              }`}>
                              {payment?.status}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-xl shadow-soft p-6">
                    <h3 className="font-heading font-semibold text-xl text-text-primary mb-6">
                      Business Profile
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Business Name
                        </label>
                        <input
                          type="text"
                          value={vendor?.businessName}
                          onChange={(e) => setVendor({ ...vendor, businessName: e.target.value })}
                          className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Your Name
                        </label>
                        <input
                          type="text"
                          value={vendor?.name}
                          onChange={(e) => setVendor({ ...vendor, name: e.target.value })}
                          className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={vendor?.email}
                          onChange={(e) => setVendor({ ...vendor, email: e.target.value })}
                          className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={vendor?.phone}
                          onChange={(e) => setVendor({ ...vendor, phone: e.target.value })}
                          className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Service Area
                        </label>
                        <input
                          type="text"
                          value={vendor?.serviceArea}
                          onChange={(e) => setVendor({ ...vendor, serviceArea: e.target.value })}
                          className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Years of Experience
                        </label>
                        <input
                          type="number"
                          value={vendor?.yearsExperience}
                          onChange={(e) => setVendor({ ...vendor, yearsExperience: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Website URL
                        </label>
                        <input
                          type="url"
                          value={vendor?.websiteUrl}
                          onChange={(e) => setVendor({ ...vendor, websiteUrl: e.target.value })}
                          className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Instagram URL
                        </label>
                        <input
                          type="url"
                          value={vendor?.instagramUrl}
                          onChange={(e) => setVendor({ ...vendor, instagramUrl: e.target.value })}
                          className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
                        />
                      </div>
                    </div>

                    <div className="mt-6 flex space-x-3">
                      <Button
                        variant="primary"
                        onClick={() => handleUpdateProfile(vendor)}
                      >
                        Update Profile
                      </Button>
                      <Button
                        variant="outline"
                        iconName="Eye"
                        onClick={() => navigate('/vendor-profile', { state: { vendorId: vendor?.id } })}
                      >
                        Preview Public Profile
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Subscription Tab */}
              {activeTab === 'subscription' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-xl shadow-soft p-6">
                    <h3 className="font-heading font-semibold text-xl text-text-primary mb-6">
                      Current Subscription
                    </h3>

                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h4 className="font-heading font-semibold text-lg text-text-primary capitalize">
                          {subscription?.type} Plan
                        </h4>
                        <p className="text-text-secondary">
                          Active until {formatDate(subscription?.endDate)}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-text-primary">
                          {formatPrice(subscription?.price)}
                        </div>
                        <div className="text-text-secondary text-sm">per year</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {subscription?.features?.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                          <span className="text-text-primary text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex space-x-4">
                      <Button
                        variant="primary"
                        onClick={() => handleUpgradeSubscription('enterprise')}
                      >
                        Upgrade Plan
                      </Button>
                      <Button variant="outline">
                        Manage Billing
                      </Button>
                    </div>
                  </div>

                  {/* Upgrade Options */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white rounded-xl shadow-soft p-6 border border-border-light">
                      <h4 className="font-heading font-semibold text-lg text-text-primary mb-2">
                        Free Plan
                      </h4>
                      <div className="text-2xl font-bold text-text-primary mb-4">$0</div>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                          <span>Basic listing</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                          <span>5 photos</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                          <span>Basic messaging</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-xl shadow-soft p-6 border-2 border-primary">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-heading font-semibold text-lg text-text-primary">
                          Premium Plan
                        </h4>
                        <span className="px-2 py-1 bg-primary text-white text-xs rounded-full">
                          Current
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-text-primary mb-4">$299</div>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                          <span>Unlimited listings</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                          <span>Priority placement</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                          <span>Advanced analytics</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                          <span>Marketing tools</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white rounded-xl shadow-soft p-6 border border-border-light">
                      <h4 className="font-heading font-semibold text-lg text-text-primary mb-2">
                        Enterprise Plan
                      </h4>
                      <div className="text-2xl font-bold text-text-primary mb-4">$599</div>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                          <span>Everything in Premium</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                          <span>Dedicated support</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                          <span>Custom integrations</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={16} className="text-green-500" />
                          <span>White-label options</span>
                        </li>
                      </ul>
                      <Button
                        variant="primary"
                        size="sm"
                        fullWidth
                        className="mt-4"
                        onClick={() => handleUpgradeSubscription('enterprise')}
                      >
                        Upgrade to Enterprise
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Service Modal */}
      {showServiceModal && (
        <ServiceModal
          service={editingService}
          onSave={handleSaveService}
          onClose={() => {
            setShowServiceModal(false);
            setEditingService(null);
          }}
        />
      )}

      <Footer />
    </div>
  );
};

export default VendorDashboard;